/**
 * 国際ドラフツ (10x10) 盤コンポーネント。
 *
 * - 黒マス 50 のみ駒を置く。 白マスは見た目だけ (= クリック不可)。
 * - 駒を click → 自分の駒なら from 選択 → 着地候補ハイライト → click で着手。
 * - **連続取り (multi-hop) は中間着地候補を順次タップ**。 同じ from の capture path の最初の hop が
 *   一致するもののみフィルタしながら絞り込む。 全 hop 確定で onMove。
 * - ダム (king) は中央に金色の王冠マーク。
 * - 引き分けカウンタ等は外部 (Play / Result) で表示する想定。 ここでは盤のみ。
 *
 * 重要な決定:
 * - **state.squares[idx]** が黒マス上の駒。 (row, col) 表示は `idxToRc` で変換する。
 *   白マス (light) は visually 描画するだけで状態を持たない。
 * - **deferred removal** の表示: ここでは「直前の取り途中」 という概念を持たないので、
 *   毎着手の最終状態だけ描画 (= ターン途中 partial state は楽観更新で 1 ply 単位)。
 * - flip: yourSide==='Second' のとき盤を 180 度回転表示 (row も col も反転)。
 */

import { useState } from 'react';
import {
  capturedIdxsFor,
  DRAUGHTS_SIZE,
  type DraughtsMove,
  type DraughtsState,
  isCapture,
  isStep,
  legalMovesFrom,
  moveFrom,
  moveLandings,
  moveTo,
  rcToIdx,
  type Side,
} from '../../games/draughts';
import { BoardCoords } from './BoardCoords';

interface Props {
  state: DraughtsState;
  legal?: DraughtsMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: DraughtsMove) => void;
  mini?: boolean;
  lastMove?: DraughtsMove;
  staticView?: boolean;
  tutorialHighlight?: [number, number][];
}

export function DraughtsBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.to_move;
  const showOppHint = !mini && !staticView && !isMyTurn;

  // 着手 UI state:
  //  - selectedFrom: 自駒タップで選んだ from idx
  //  - hopsTaken: 連続取りで既に確定した中間着地点列 (= 確定した capture path の prefix)
  const [selectedFrom, setSelectedFrom] = useState<number | null>(null);
  const [hopsTaken, setHopsTaken] = useState<number[]>([]);

  const flip = yourSide === 'Second';
  const rows = flip
    ? Array.from({ length: DRAUGHTS_SIZE }, (_, i) => DRAUGHTS_SIZE - 1 - i)
    : Array.from({ length: DRAUGHTS_SIZE }, (_, i) => i);
  const cols = flip
    ? Array.from({ length: DRAUGHTS_SIZE }, (_, i) => DRAUGHTS_SIZE - 1 - i)
    : Array.from({ length: DRAUGHTS_SIZE }, (_, i) => i);

  // 現在の選択 from で「次の hop 候補 (idx) → matching move」 を計算。
  // - selectedFrom が null なら、 全 legal から from idx の set を返す (= 自駒ハイライト)
  // - selectedFrom があり hopsTaken が空: 全 capture/step の最初の landing 候補
  // - selectedFrom があり hopsTaken: capture path の prefix が hopsTaken と一致するものの「次の hop」
  const candidateMoves: DraughtsMove[] = (() => {
    if (selectedFrom == null) return [];
    const from = selectedFrom;
    const fromLegals = legalMovesFrom(legal ?? [], from);
    if (hopsTaken.length === 0) return fromLegals;
    return fromLegals.filter((m) => {
      if (!isCapture(m)) return false;
      if (m.Capture.path.length < hopsTaken.length) return false;
      for (let i = 0; i < hopsTaken.length; i++) {
        if (m.Capture.path[i] !== hopsTaken[i]) return false;
      }
      return true;
    });
  })();

  /** 次に提示するべき land 候補 (idx → matching moves)。 */
  const nextLandings = new Map<number, DraughtsMove[]>();
  for (const m of candidateMoves) {
    let target: number;
    if (isStep(m)) {
      target = m.Step.to;
    } else {
      // capture: 次の hop = hopsTaken.length 番目の landing
      target = m.Capture.path[hopsTaken.length];
      if (target === undefined) continue;
    }
    const arr = nextLandings.get(target) ?? [];
    arr.push(m);
    nextLandings.set(target, arr);
  }

  // 現在進行中の取り経路で「deferred removal 中の半透明駒」 を計算 (= hopsTaken 列の各 hop で踏んだ enemy 駒)
  // multi-hop の partial state は持たない簡易版: hopsTaken の prefix に一致する 1 つの move を選び、 そこから capturedIdxs を計算。
  const deferredCaptured: Set<number> = (() => {
    const out = new Set<number>();
    if (hopsTaken.length === 0 || selectedFrom == null) return out;
    const sample = candidateMoves.find((m) => isCapture(m));
    if (!sample || !isCapture(sample)) return out;
    // partial: prefix までで capture された駒を計算
    const partialMv: DraughtsMove = {
      Capture: { from: sample.Capture.from, path: hopsTaken },
    };
    for (const idx of capturedIdxsFor(state, partialMv)) out.add(idx);
    return out;
  })();

  const handleCellClick = (r: number, c: number) => {
    if (mini || !isMyTurn) return;
    const idx = rcToIdx(r, c);
    if (idx == null) return; // 白マス
    const cell = state.squares[idx];

    // 自駒タップ → from を新規選択 (= 現在の hop 状態をリセット)
    const owner = cell === 'WhiteMan' || cell === 'WhiteKing'
      ? 'First'
      : cell === 'BlackMan' || cell === 'BlackKing'
        ? 'Second'
        : null;
    if (owner === state.to_move) {
      // 既に selectedFrom が一致しているなら何もしない (連続取りの途中)
      if (selectedFrom !== idx || hopsTaken.length > 0) {
        setSelectedFrom(idx);
        setHopsTaken([]);
      }
      return;
    }

    if (selectedFrom == null) return;

    // 着地候補に該当?
    const moves = nextLandings.get(idx);
    if (!moves || moves.length === 0) {
      // 候補外: 選択解除
      setSelectedFrom(null);
      setHopsTaken([]);
      return;
    }

    // 着手 OR 次 hop:
    //  - step or capture で path.length === hopsTaken.length + 1 のものは確定
    //  - capture で path.length > hopsTaken.length + 1 のものは継続
    const finishes = moves.filter((m) => {
      if (isStep(m)) return true;
      return m.Capture.path.length === hopsTaken.length + 1;
    });
    const continues = moves.filter(
      (m) => isCapture(m) && m.Capture.path.length > hopsTaken.length + 1,
    );

    // 同じ着地で「終了するパス」 と 「続くパス」 が両方ある場合は、 強制取りルール上は
    // path 長が最大のもののみが legal に入っているはず (= 続くパスのみが残る) → finishes は出ない
    // が、 安全のため continues 優先で次 hop に進める。
    if (continues.length > 0) {
      setHopsTaken([...hopsTaken, idx]);
      return;
    }
    if (finishes.length > 0) {
      const mv = finishes[0]; // 同一着地で複数 finish (= 同じ path) はあり得ないので [0]
      onMove?.(mv);
      setSelectedFrom(null);
      setHopsTaken([]);
    }
  };

  // 着手候補 (緑ハイライト) の set
  const candidateLandings = new Set(Array.from(nextLandings.keys()));

  // last-move のハイライト (起点 + 全 landing)
  const lastHighlight = new Set<number>();
  if (lastMove && !mini) {
    lastHighlight.add(moveFrom(lastMove));
    lastHighlight.add(moveTo(lastMove));
    for (const l of moveLandings(lastMove)) lastHighlight.add(l);
  }

  return (
    <div className={`draughts-frame ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
      <div className="draughts-board">
        {/* 座標は升の外 (盤の縁) に。 升の中だと駒に隠れる。 表示順で渡すので反転も追従する。 */}
        {!mini && (
          <BoardCoords
            files={cols.map((c) => String.fromCharCode(97 + c))}
            ranks={rows.map((r) => `${r + 1}`)}
          />
        )}
        {rows.flatMap((r) =>
          cols.map((c) => {
            const isDark = r % 2 === 0 ? c % 2 === 1 : c % 2 === 0;
            const idx = rcToIdx(r, c);
            const cell = idx != null ? state.squares[idx] : 'Empty';
            const isSelected = selectedFrom != null && idx === selectedFrom;
            const isCandidate = idx != null && candidateLandings.has(idx);
            const isLast = idx != null && lastHighlight.has(idx);
            const isHopTaken = idx != null && hopsTaken.includes(idx);
            const isDeferred = idx != null && deferredCaptured.has(idx);
            const isTutorialHint =
              tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
            const rankLabel = `${r + 1}`;
            const fileLabel = String.fromCharCode('a'.charCodeAt(0) + c);
            return (
              <button
                key={`${r}-${c}`}
                className={`cell ${isDark ? 'dark' : 'light'} ${isSelected ? 'selected' : ''} ${
                  isCandidate ? 'cand' : ''
                } ${isLast ? 'last-move' : ''} ${isHopTaken ? 'hop-taken' : ''} ${
                  isTutorialHint ? 'tutorial-hint' : ''
                }`}
                disabled={mini || !isDark}
                onClick={() => handleCellClick(r, c)}
                aria-label={`${fileLabel}${rankLabel}`}
              >
                {cell !== 'Empty' && (
                  <span
                    className={`draughts-disk ${
                      cell === 'WhiteMan' || cell === 'WhiteKing' ? 'white' : 'black'
                    } ${cell === 'WhiteKing' || cell === 'BlackKing' ? 'king' : ''} ${
                      isDeferred ? 'deferred' : ''
                    }`}
                  >
                    {(cell === 'WhiteKing' || cell === 'BlackKing') && (
                      <span className="draughts-crown" aria-hidden>♛</span>
                    )}
                  </span>
                )}
              </button>
            );
          }),
        )}
      </div>
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}
